import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { IRestaurantNode } from 'generated/rbi-graphql';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useGetRestaurantAvailabilityFn, useIsMobileOrderingAvailable } from 'utils/restaurant';
import { PICKUP_SERVICE_MODES } from 'utils/service-mode';

interface UseErrorMessageOptions {
  restaurant: IRestaurantNode;
}

export const useMessage = ({ restaurant }: UseErrorMessageOptions) => {
  const { formatMessage } = useIntl();
  const { serviceModeStatus } = useServiceModeStatus(restaurant);
  const getRestaurantAvailability = useGetRestaurantAvailabilityFn();
  const isMobileOrderingAvailable = useIsMobileOrderingAvailable(restaurant);

  const isRestaurantAvailable = getRestaurantAvailability(restaurant);
  const isRestaurantOpen = PICKUP_SERVICE_MODES.some(
    serviceMode => serviceModeStatus[serviceMode].available
  );
  const hideMobileOrdering = useFlag(LaunchDarklyFlag.HIDE_MOBILE_ORDERING);

  return useMemo(() => {
    if (restaurant.hasMobileOrdering && !isRestaurantOpen) {
      return formatMessage({ id: 'locationClosed' });
    }

    if (!restaurant.hasMobileOrdering) {
      if (!hideMobileOrdering) {
        return formatMessage({ id: 'storeDoesNotHaveMobileOrdering' });
      }
    }

    if (!isMobileOrderingAvailable || !isRestaurantAvailable) {
      if (!hideMobileOrdering) {
        return formatMessage({ id: 'mobileOrderingUnavailable' });
      }
    }

    return;
  }, [
    formatMessage,
    hideMobileOrdering,
    isMobileOrderingAvailable,
    isRestaurantAvailable,
    isRestaurantOpen,
    restaurant.hasMobileOrdering,
  ]);
};
